import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { Card, Table, Badge, Button, LoadingSkeleton } from '../ui';
import useApi from '../portal/useApi';
import { formatMoney, formatDate, classLabel } from '../portal/format';
import { downloadFile } from '../portal/download';
import ReportView from '../reports/ReportView';
import styles from '../portal/Portal.module.css';

const ATTENDANCE_BADGE = { present: 'success', late: 'warning', absent: 'danger', excused: 'info' };
const FEE_BADGE = { paid: 'success', partial: 'warning', pending: 'default', overdue: 'danger' };
const COMMENDATION_CATEGORIES = { discipline: 'Good discipline', academic: 'Academic', sports: 'Sports', leadership: 'Leadership', service: 'Service', other: 'Other' };

function Section({ state, children }) {
  if (state.loading) return <LoadingSkeleton type="text" rows={4} />;
  if (state.error) return <div className="alert alert-error">{state.error}</div>;
  return children;
}

function ProfileTab({ student }) {
  return (
    <Card>
      <dl className={styles.details}>
        <div><dt>First names</dt><dd>{student.first_names || student.full_name}</dd></div>
        <div><dt>Surname</dt><dd>{student.surname || '—'}</dd></div>
        <div><dt>Class</dt><dd>{classLabel(student)}</dd></div>
        <div><dt>Date of birth</dt><dd>{formatDate(student.date_of_birth)}</dd></div>
        <div><dt>Gender</dt><dd>{student.gender || '—'}</dd></div>
        <div><dt>Address</dt><dd>{student.address || '—'}</dd></div>
        {(student.guardians || []).map((guardian, index) => (
          <div key={guardian.id}>
            <dt>{guardian.relationship || 'Guardian'}{index === 0 ? ' (main contact)' : ''}</dt>
            <dd>{[guardian.full_name, guardian.phone, guardian.email].filter(Boolean).join(' · ')}</dd>
          </div>
        ))}
        {!(student.guardians || []).length && <div><dt>Guardian on record</dt><dd>—</dd></div>}
        {student.blood_group && <div><dt>Blood group</dt><dd>{student.blood_group}</dd></div>}
        {student.allergies && <div><dt>Allergies</dt><dd>{student.allergies}</dd></div>}
        {student.medical_conditions && <div><dt>Medical conditions</dt><dd>{student.medical_conditions}</dd></div>}
        {student.medications && <div><dt>Regular medication</dt><dd>{student.medications}</dd></div>}
        {student.medical_notes && <div><dt>Medical notes</dt><dd>{student.medical_notes}</dd></div>}
      </dl>
      <p className={styles.muted} style={{ marginTop: '1rem' }}>
        If any of these details are wrong, please let the school office know.
      </p>
    </Card>
  );
}

// Published term reports only: a report (and that term's marks) reaches
// the family once the Head of Academics has published it.
function ReportsTab({ token, studentId, schoolName }) {
  const cards = useApi(token, `/api/v1/reports/cards/student/${studentId}`);
  const [selectedId, setSelectedId] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  const list = cards.data || [];
  const activeId = selectedId || (list[0] ? String(list[0].id) : '');
  const report = useApi(token, activeId ? `/api/v1/reports/cards/${activeId}` : null);

  const handleDownload = async () => {
    setDownloading(true);
    setError(await downloadFile(token, `/api/v1/reports/cards/${activeId}/pdf`, 'report.pdf'));
    setDownloading(false);
  };

  return (
    <Section state={cards}>
      {list.length === 0 ? (
        <Card>
          <p className={styles.muted}>
            No report has been published yet. Term reports appear here, with the term's marks, once the school has finalised and published them.
          </p>
        </Card>
      ) : (
        <div>
          {error && <div className="alert alert-error">{error}</div>}
          <div className={styles.toolbar}>
            <label htmlFor="report-term">Report</label>
            <select id="report-term" className={styles.select} value={activeId} onChange={(e) => setSelectedId(e.target.value)}>
              {list.map((card) => <option key={card.id} value={card.id}>{card.term_label}</option>)}
            </select>
            <Button variant="primary" size="sm" onClick={handleDownload} disabled={downloading || !report.data}>
              <ArrowDownTrayIcon style={{ width: 16, height: 16 }} />
              {downloading ? 'Preparing…' : 'Download PDF'}
            </Button>
          </div>
          <Section state={report}>
            {report.data && <ReportView report={report.data} schoolName={schoolName} />}
          </Section>
        </div>
      )}
    </Section>
  );
}

function AttendanceTab({ token, studentId }) {
  const attendance = useApi(token, `/api/v1/attendance/students/${studentId}`);
  const records = [...(attendance.data || [])].sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  const count = (status) => records.filter((record) => record.status === status).length;

  return (
    <Section state={attendance}>
      <div className={styles.statsOnPage}>
        {['present', 'late', 'absent', 'excused'].map((status) => (
          <div key={status} className={styles.stat}>
            <div className={styles.statLabel}>{status.charAt(0).toUpperCase() + status.slice(1)}</div>
            <div className={styles.statValue}>{count(status)}</div>
          </div>
        ))}
      </div>
      <Card>
        <Table
          columns={[
            { header: 'Date', render: (row) => formatDate(row.date) },
            { header: 'Session', render: (row) => (row.session_type === 'homeroom' ? 'Roll call' : 'Lesson') },
            { header: 'Status', render: (row) => <Badge variant={ATTENDANCE_BADGE[row.status] || 'default'}>{row.status}</Badge> },
            { header: 'Remarks', render: (row) => row.remarks || '—' },
          ]}
          data={records}
          emptyMessage="No attendance has been recorded yet."
        />
      </Card>
    </Section>
  );
}

function FeesTab({ token, studentId, features }) {
  const arrears = useApi(token, `/api/v1/students/${studentId}/arrears`);
  const fees = useApi(token, `/api/v1/fees?student_id=${studentId}`);
  const payments = useApi(token, features.payments !== false ? `/api/v1/payments?student_id=${studentId}` : null);
  const plans = useApi(token, features.payment_plans !== false ? `/api/v1/students/${studentId}/payment-plans` : null);

  return (
    <div>
      <Section state={arrears}>
        <div className={styles.statsOnPage}>
          <div className={styles.stat}>
            <div className={styles.statLabel}>Total fees charged</div>
            <div className={styles.statValue}>{formatMoney(arrears.data?.total_fees_usd)}</div>
          </div>
          <div className={styles.stat}>
            <div className={styles.statLabel}>Total paid</div>
            <div className={styles.statValue}>{formatMoney(arrears.data?.total_payments_usd)}</div>
          </div>
          <div className={styles.stat}>
            <div className={styles.statLabel}>Outstanding</div>
            <div className={`${styles.statValue} ${arrears.data?.arrears_usd > 0 ? styles.statBad : styles.statGood}`}>{formatMoney(arrears.data?.arrears_usd)}</div>
          </div>
        </div>
      </Section>

      <h3 className={styles.sectionTitle}>Fees charged</h3>
      <Section state={fees}>
        <Card>
          <Table
            columns={[
              { header: 'Term', render: (row) => (row.term ? `Term ${row.term}, ${row.year}` : '—') },
              { header: 'Type', render: (row) => row.fee_type || 'Fees' },
              { header: 'Amount', render: (row) => formatMoney(row.amount, row.currency) },
              { header: 'Due', render: (row) => formatDate(row.due_date) },
              { header: 'Status', render: (row) => <Badge variant={FEE_BADGE[row.status] || 'default'}>{row.status}</Badge> },
            ]}
            data={fees.data || []}
            emptyMessage="No fees have been charged yet."
          />
        </Card>
      </Section>

      {features.payments !== false && (
        <>
          <h3 className={styles.sectionTitle}>Payments received</h3>
          <Section state={payments}>
            <Card>
              <Table
                columns={[
                  { header: 'Date', render: (row) => formatDate(row.payment_date) },
                  { header: 'Receipt', accessor: 'receipt_number' },
                  { header: 'Amount', render: (row) => formatMoney(row.amount, row.currency) },
                  { header: 'Method', render: (row) => row.payment_method || '—' },
                ]}
                data={payments.data || []}
                emptyMessage="No payments have been recorded yet."
              />
            </Card>
          </Section>
        </>
      )}

      {features.payment_plans !== false && (plans.data || []).length > 0 && (
        <>
          <h3 className={styles.sectionTitle}>Payment plans</h3>
          <Card>
            <Table
              columns={[
                { header: 'Total', render: (row) => formatMoney(row.total_amount, row.currency) },
                { header: 'Instalment', render: (row) => `${formatMoney(row.installment_amount, row.currency)} × ${row.number_of_installments}` },
                { header: 'Next payment', render: (row) => formatDate(row.next_payment_date) },
                { header: 'Status', render: (row) => <Badge variant={row.status === 'active' ? 'info' : row.status === 'completed' ? 'success' : 'danger'}>{row.status}</Badge> },
              ]}
              data={plans.data}
            />
          </Card>
        </>
      )}
    </div>
  );
}

function ActivitiesTab({ token, studentId, features }) {
  const sportsOn = features.sports !== false;
  const clubsOn = features.clubs !== false;
  const sports = useApi(token, sportsOn ? `/api/v1/sports/students/${studentId}` : null);
  const achievements = useApi(token, sportsOn ? `/api/v1/sports/students/${studentId}/achievements` : null);
  const clubs = useApi(token, clubsOn ? `/api/v1/students/${studentId}/clubs` : null);

  return (
    <div>
      {sportsOn && (
        <>
          <h3 className={styles.sectionTitle}>Sports</h3>
          <Section state={sports}>
            <Card>
              <Table
                columns={[
                  { header: 'Sport', accessor: 'sport' },
                  { header: 'Position', render: (row) => row.position || '—' },
                ]}
                data={sports.data || []}
                emptyMessage="Not registered for any sport yet."
              />
            </Card>
          </Section>

          <h3 className={styles.sectionTitle}>Sports achievements</h3>
          <Section state={achievements}>
            <Card>
              <Table
                columns={[
                  { header: 'Date', render: (row) => formatDate(row.date_awarded) },
                  { header: 'Sport', accessor: 'sport' },
                  { header: 'Achievement', accessor: 'title' },
                  { header: 'Level', render: (row) => row.level || '—' },
                  { header: 'Details', render: (row) => row.description || '—' },
                ]}
                data={achievements.data || []}
                emptyMessage="No sports achievements recorded yet."
              />
            </Card>
          </Section>
        </>
      )}

      {clubsOn && (
        <>
          <h3 className={styles.sectionTitle}>Clubs</h3>
          <Section state={clubs}>
            <Card>
              <Table
                columns={[
                  { header: 'Club', accessor: 'club_name' },
                  { header: 'Role', render: (row) => row.role || 'Member' },
                  { header: 'Joined', render: (row) => formatDate(row.join_date) },
                ]}
                data={clubs.data || []}
                emptyMessage="Not a member of any club yet."
              />
            </Card>
          </Section>
        </>
      )}
    </div>
  );
}

function HouseTab({ token, student }) {
  const standings = useApi(token, '/api/v1/houses/standings');
  const scores = useApi(token, `/api/v1/houses/scores?student_id=${student.id}`);
  const commendations = useApi(token, `/api/v1/houses/board?student_id=${student.id}`);

  const earned = (scores.data || []).reduce((sum, score) => sum + score.points, 0);
  const house = (standings.data || []).find((row) => row.id === student.house_id);

  return (
    <div>
      <div className={styles.statsOnPage}>
        <div className={styles.stat}>
          <div className={styles.statLabel}>House</div>
          <div className={styles.statValue}>{student.house || 'Not in a house yet'}</div>
        </div>
        <div className={styles.stat}>
          <div className={styles.statLabel}>House position</div>
          <div className={styles.statValue}>{house ? `#${house.rank} of ${standings.data.length}` : '—'}</div>
        </div>
        <div className={styles.stat}>
          <div className={styles.statLabel}>Points earned for the house</div>
          <div className={styles.statValue}>{earned}</div>
        </div>
      </div>

      <h3 className={styles.sectionTitle}>House standings</h3>
      <Section state={standings}>
        <Card>
          <Table
            columns={[
              { header: 'Position', render: (row) => `#${row.rank}` },
              { header: 'House', render: (row) => (row.id === student.house_id ? <strong>{row.name}</strong> : row.name) },
              { header: 'Points', accessor: 'points' },
              { header: 'Students', accessor: 'member_count' },
            ]}
            data={standings.data || []}
            emptyMessage="The school has not set up houses yet."
          />
        </Card>
      </Section>

      <h3 className={styles.sectionTitle}>Points earned</h3>
      <Section state={scores}>
        <Card>
          <Table
            columns={[
              { header: 'Date', render: (row) => formatDate(row.date) },
              { header: 'Activity', accessor: 'activity' },
              { header: 'House', accessor: 'house_name' },
              { header: 'Points', render: (row) => (row.points > 0 ? `+${row.points}` : row.points) },
            ]}
            data={scores.data || []}
            emptyMessage="No house points have been recorded yet."
          />
        </Card>
      </Section>

      <h3 className={styles.sectionTitle}>Commendations</h3>
      <Section state={commendations}>
        <Card>
          <Table
            columns={[
              { header: 'Date', render: (row) => formatDate(row.date) },
              { header: 'Category', render: (row) => <Badge variant="success">{COMMENDATION_CATEGORIES[row.category] || row.category}</Badge> },
              { header: 'Commendation', accessor: 'title' },
              { header: 'Details', render: (row) => row.description || '—' },
            ]}
            data={commendations.data || []}
            emptyMessage="No commendations have been recorded yet."
          />
        </Card>
      </Section>
    </div>
  );
}

// One child's page for a parent:profile, reports, attendance and fees,
// each tab only shown when the school has that module switched on.
function ChildDetail({ token, user }) {
  const { id } = useParams();
  const features = user?.features || {};
  const student = useApi(token, `/api/v1/students/${id}`);
  const [tab, setTab] = useState('profile');

  const tabs = [
    { key: 'profile', label: 'Profile' },
    { key: 'reports', label: 'Reports' },
    features.attendance !== false && { key: 'attendance', label: 'Attendance' },
    features.fees !== false && { key: 'fees', label: 'Fees' },
    (features.sports !== false || features.clubs !== false) && { key: 'activities', label: 'Activities' },
    features.houses !== false && { key: 'house', label: 'House' },
  ].filter(Boolean);

  return (
    <div>
      <Link to="/my-children" className={styles.backLink}>
        <ArrowLeftIcon style={{ width: 14, height: 14 }} />
        My Children
      </Link>

      <Section state={student}>
        {student.data && (
          <>
            <div className={styles.header}>
              <div>
                <h2 className={styles.title}>{student.data.full_name}</h2>
                <p className={styles.subtitle}>{classLabel(student.data)}</p>
              </div>
            </div>

            <div className={styles.tabs} role="tablist">
              {tabs.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  role="tab"
                  aria-selected={tab === item.key}
                  className={tab === item.key ? styles.tabActive : styles.tab}
                  onClick={() => setTab(item.key)}
                >
                  {item.label}
                </button>
              ))}
            </div>

            {tab === 'profile' && <ProfileTab student={student.data} />}
            {tab === 'reports' && <ReportsTab token={token} studentId={id} schoolName={user?.school_name} />}
            {tab === 'attendance' && <AttendanceTab token={token} studentId={id} />}
            {tab === 'fees' && <FeesTab token={token} studentId={id} features={features} />}
            {tab === 'activities' && <ActivitiesTab token={token} studentId={id} features={features} />}
            {tab === 'house' && <HouseTab token={token} student={student.data} />}
          </>
        )}
      </Section>
    </div>
  );
}

export default ChildDetail;
